import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { CustomFieldTypeFromService } from '../context/CustomField/CustomFieldTypes'

interface CustomFieldQueryProps {
    fieldEId: string
    objectType: CustomFieldTypeFromService['objectType']
    valueEIds?: Array<string>
}
export default function useCustomFieldQuery(props: CustomFieldQueryProps) {
    const [resultsLoading, setResultsLoading] = useState(false)
    const [pageLoading, setPageLoading] = useState(false)
    const [data, setData] = useState<Array<{ key: string, value: string }>>([])
    const [currentPage, setCurrentPage] = useState(0)
    const denyCall = useRef(true)

    const fetchSelectedResults = async (keywords = '') => {
        setResultsLoading(true)
        setPageLoading(false)
        setCurrentPage(0)

        try {
            const fetchResponse = await fetchData({
                objectType: props.objectType,
                fieldEId: props.fieldEId,
                keywords: keywords,
                valueEIds: props.valueEIds,
                page: 0,
            })

            if (fetchResponse.status === 'error') {
                setResultsLoading(false)
                console.error(`Failed to fetch initial query results`, {
                    objectType: props.objectType,
                    fieldEId: props.fieldEId,
                    keywords: '',
                    page: 0,
                    message: fetchResponse.message,
                    stack: fetchResponse.stack,
                })
                return
            }

            setData(fetchResponse.results || [])
            setResultsLoading(false)
        } catch (err) {
            setResultsLoading(false)
            console.error(`Failed to fetch initial query results`, {
                objectType: props.objectType,
                fieldEId: props.fieldEId,
                keywords: '',
                page: 0,
                message: err.toString(),
                stack: err.toString(),
            })
        }
    }

    const fetchFirstPageResults = async (keywords = '') => {
        setResultsLoading(true)
        setPageLoading(false)
        setCurrentPage(0)

        try {
            const fetchResponse = await fetchData({
                objectType: props.objectType,
                fieldEId: props.fieldEId,
                keywords: keywords,
                page: 0,
            })

            if (fetchResponse.status === 'error') {
                setResultsLoading(false)
                console.error(`Failed to fetch initial query results`, {
                    objectType: props.objectType,
                    fieldEId: props.fieldEId,
                    keywords: '',
                    page: 0,
                    message: fetchResponse.message,
                    stack: fetchResponse.stack,
                })
                return
            }

            setData(fetchResponse.results || [])
            setResultsLoading(false)
        } catch (err) {
            setResultsLoading(false)
            console.error(`Failed to fetch initial query results`, {
                objectType: props.objectType,
                fieldEId: props.fieldEId,
                keywords: '',
                page: 0,
                message: err.toString(),
                stack: err.toString(),
            })
        }
    }

    // Based on the selected field we will need to fetch the appropriate values
    useEffect(() => {
        setData([])
    }, [props.fieldEId])

    const handleKeyStroke = useCallback(async (val: string) => {
        if (denyCall.current) {
            denyCall.current = false
            return
        }
        fetchFirstPageResults(val)
    }, [props.objectType, props.fieldEId])

    const handleFetchNextPage = useCallback(async (val: string, page: number) => {
        setPageLoading(true)

        try {
            const fetchResponse = await fetchData({
                objectType: props.objectType,
                fieldEId: props.fieldEId,
                keywords: val,
                page: page + 1,
            })

            if (fetchResponse.status === 'error') {
                setPageLoading(false)
                console.error(`Failed to fetch paged query results`, {
                    objectType: props.objectType,
                    fieldEId: props.fieldEId,
                    keywords: val,
                    page,
                    message: fetchResponse.message,
                    stack: fetchResponse.stack,
                })
                return
            }

            if (!fetchResponse || !fetchResponse.results || fetchResponse.results.length === 0) {
                setPageLoading(false)
            } else {
                setData(currentData => [...currentData, ...fetchResponse.results])
                setPageLoading(false)
                setCurrentPage(currentPage => page + 1)
            }

        } catch (err) {
            setPageLoading(false)
            console.error(`Failed to fetch paged query results`, {
                objectType: props.objectType,
                fieldEId: props.fieldEId,
                keywords: val,
                page,
                message: err.toString(),
                stack: err.toString(),
            })
        }
    }, [props.objectType, props.fieldEId])

    return useMemo(() => ({
        resultsLoading,
        pageLoading,
        data,
        setData,
        currentPage,
        fetchSelectedResults,
        fetchFirstPageResults,
        handleKeyStroke,
        handleFetchNextPage,
    }), [
        resultsLoading,
        pageLoading,
        data,
        setData,
        currentPage,
        fetchSelectedResults,
        fetchFirstPageResults,
        handleKeyStroke,
        handleFetchNextPage,
    ])
}

interface FetchOptions {
    fieldEId: string
    objectType: CustomFieldTypeFromService['objectType']
    keywords: string
    page: number
    valueEIds?: Array<string>
}
type FetchReturnValue = Promise<{
    status: 'error' | 'success'
    httpCode: number | null
    message: string
    stack: any
    results: Array<{ key: string, value: string }>
}>
const fetchData = ({ objectType, fieldEId, page, keywords, valueEIds }: FetchOptions): FetchReturnValue => {
    return new Promise(function (resolve, reject) {
        const params = new URLSearchParams({
            "keywords": keywords || '',
            "page": String(page),
            "fieldEId": fieldEId,
            "objectType": objectType as string,
            "valueEIds": valueEIds && Array.isArray(valueEIds) ? valueEIds.join(',') : ''
        }).toString()

        // @ts-ignore
        fetch(`${window.jvUrls.admin}/action/offer-form/field-values?${params}`)
            .then(response =>
                response.json()
                    .then((data: any) => {
                        resolve({
                            status: 'success',
                            httpCode: response.status,
                            message: '',
                            stack: null,
                            results: data.fieldValues || []
                        })
                    }).catch(err => {
                        reject({
                            status: 'error',
                            httpCode: null,
                            message: 'Failed to parse results',
                            stack: err.toString(),
                            results: [],
                        })
                    })
            )
            .catch(err => {
                reject({
                    status: 'error',
                    httpCode: null,
                    message: 'Failed to fetch results',
                    stack: err.toString(),
                    results: [],
                })
            })
    })
}